import { useEffect, useMemo, useState } from "react";
import * as DocumentPicker from "expo-document-picker";
import { Alert, StyleSheet, View } from "react-native";

import {
  AppText,
  Button,
  ButtonText,
  Card,
  ProtectedScreen,
  SearchableSelect,
  Stack,
  TextField,
} from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { useWorkspaceReadOnly } from "~/hooks/useWorkspaceReadOnly";
import {
  usePrimaryWorkspace,
  useWorkspaceClients,
  useWorkspaceMembership,
  useWorkspaceProjects,
} from "~/hooks/workspace";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { saveCsvExport } from "~/utils/export-file";
import type { Tables } from "~/api/tables";
import { openExternalUrl } from "~/utils/external-link";
import { removeFile, signedFileUrl, uploadFile } from "~/api/uploads";
import { db } from "~/api/db";

type Expense = Tables<"expenses"> & {
  expense_categories: { name: string } | null;
};
type Category = Tables<"expense_categories">;

function money(cents: number) {
  return `NZ$${(cents / 100).toFixed(2)}`;
}

function safeFilename(value: string) {
  return value.replace(/[^a-zA-Z0-9._-]/g, "-").slice(-120);
}

export default function ExpensesPage() {
  const styles = useThemedStyles(makeStyles);
  const { user } = useAuth();
  const workspace = usePrimaryWorkspace();
  const clients = useWorkspaceClients(workspace.data?.id);
  const projects = useWorkspaceProjects(workspace.data?.id);
  const membership = useWorkspaceMembership(workspace.data?.id);
  const { readOnly } = useWorkspaceReadOnly(workspace.data?.id);
  const workspaceId = workspace.data?.id;
  const canWrite =
    !readOnly &&
    Boolean(
      workspace.data?.owner_id === user?.id ||
      membership.data?.role === "owner" ||
      membership.data?.role === "admin" ||
      membership.data?.role === "member",
    );
  const [rows, setRows] = useState<Expense[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [rebillFilter, setRebillFilter] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [supplier, setSupplier] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [rebillable, setRebillable] = useState("false");
  const [total, setTotal] = useState("");
  const [gst, setGst] = useState("0");
  const [businessUse, setBusinessUse] = useState("100");
  const [notes, setNotes] = useState("");
  const [receipt, setReceipt] =
    useState<DocumentPicker.DocumentPickerAsset | null>(null);
  const [newCategory, setNewCategory] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    if (!workspaceId) return;
    const [
      { data: expenseData, error },
      { data: categoryData, error: categoryError },
    ] = await Promise.all([
      db
        .from("expenses")
        .select("*,expense_categories(name)")
        .eq("workspace_id", workspaceId)
        .order("expense_date", { ascending: false }),
      db
        .from("expense_categories")
        .select("*")
        .eq("workspace_id", workspaceId)
        .is("archived_at", null)
        .order("sort_order"),
    ]);
    if (error || categoryError) {
      Alert.alert(
        "Could not load expenses",
        error?.message ?? categoryError?.message,
      );
      return;
    }
    setRows((expenseData ?? []) as Expense[]);
    setCategories((categoryData ?? []) as Category[]);
  }

  useEffect(() => {
    void load();
  }, [workspaceId]);

  const clientById = useMemo(
    () => new Map((clients.data ?? []).map((client) => [client.id, client])),
    [clients.data],
  );
  const projectById = useMemo(
    () =>
      new Map((projects.data ?? []).map((project) => [project.id, project])),
    [projects.data],
  );
  const clientOptions = [
    { label: "No client", value: "" },
    ...(clients.data ?? []).map((client) => ({
      label: client.name,
      value: client.id,
    })),
  ];
  const projectOptions = [
    { label: "No project", value: "" },
    ...(projects.data ?? [])
      .filter((project) => !clientId || project.client_id === clientId)
      .map((project) => ({ label: project.name, value: project.id })),
  ];
  const filtered = useMemo(
    () =>
      rows.filter(
        (row) =>
          (!from || row.expense_date >= from) &&
          (!to || row.expense_date <= to) &&
          (!categoryFilter || row.category_id === categoryFilter) &&
          (!rebillFilter ||
            (rebillFilter === "ready" &&
              row.rebillable &&
              !row.rebilled_invoice_id) ||
            (rebillFilter === "rebilled" &&
              Boolean(row.rebilled_invoice_id))) &&
          (!query ||
            `${row.supplier} ${row.description} ${row.expense_categories?.name ?? ""}`
              .toLowerCase()
              .includes(query.toLowerCase())),
      ),
    [rows, from, to, query, categoryFilter, rebillFilter],
  );

  function resetForm() {
    setEditingId(null);
    setDate(new Date().toISOString().slice(0, 10));
    setSupplier("");
    setDescription("");
    setCategoryId("");
    setClientId("");
    setProjectId("");
    setRebillable("false");
    setTotal("");
    setGst("0");
    setBusinessUse("100");
    setNotes("");
    setReceipt(null);
  }

  function edit(row: Expense) {
    setEditingId(row.id);
    setDate(row.expense_date);
    setSupplier(row.supplier);
    setDescription(row.description);
    setCategoryId(row.category_id ?? "");
    setClientId(row.client_id ?? "");
    setProjectId(row.project_id ?? "");
    setRebillable(row.rebillable ? "true" : "false");
    setTotal((row.total_cents / 100).toFixed(2));
    setGst((row.entered_gst_cents / 100).toFixed(2));
    setBusinessUse(String(row.business_use_percentage));
    setNotes(row.notes ?? "");
    setReceipt(null);
  }

  async function pickReceipt() {
    const result = await DocumentPicker.getDocumentAsync({
      type: ["image/jpeg", "image/png", "application/pdf"],
      copyToCacheDirectory: true,
    });
    if (result.canceled) return;
    const asset = result.assets[0];
    if (!asset) return;
    if (asset.size && asset.size > 10 * 1024 * 1024) {
      Alert.alert(
        "Receipt too large",
        "Choose a JPG, PNG, or PDF up to 10 MB.",
      );
      return;
    }
    setReceipt(asset);
  }

  async function uploadReceipt(expenseId: string, oldPath: string | null) {
    if (!receipt || !workspaceId || !user) return null;
    const mimeType = receipt.mimeType ?? "application/octet-stream";
    if (!["image/jpeg", "image/png", "application/pdf"].includes(mimeType)) {
      throw new Error("Receipt must be a JPG, PNG, or PDF.");
    }
    const fileBody = receipt.file
      ? await receipt.file.arrayBuffer()
      : await (await fetch(receipt.uri)).arrayBuffer();
    const stored = await uploadFile({
      workspaceId,
      purpose: "receipt",
      body: fileBody,
      mimeType,
    });
    if (oldPath && oldPath !== stored.path) await removeFile(oldPath);
    return {
      receipt_path: stored.path,
      receipt_filename: receipt.name,
      receipt_mime_type: mimeType,
      receipt_size_bytes: receipt.size ?? fileBody.byteLength,
    };
  }

  async function save() {
    if (!workspaceId || !user || !canWrite) return;
    const totalCents = Math.round(Number(total) * 100);
    const gstCents = Math.round(Number(gst) * 100);
    const businessUsePercentage = Number(businessUse);
    const shouldRebill = rebillable === "true";
    if (
      !supplier.trim() ||
      !description.trim() ||
      !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
      !Number.isFinite(totalCents) ||
      totalCents <= 0 ||
      gstCents < 0 ||
      gstCents > totalCents ||
      !Number.isFinite(businessUsePercentage) ||
      businessUsePercentage < 0 ||
      businessUsePercentage > 100 ||
      (shouldRebill && !clientId)
    ) {
      Alert.alert(
        "Check expense",
        "Enter a date, supplier, description, positive total, valid GST, business use from 0 to 100%, and a client for rebillable costs.",
      );
      return;
    }
    setSaving(true);
    try {
      const values = {
        workspace_id: workspaceId,
        category_id: categoryId || null,
        client_id: clientId || null,
        project_id: projectId || null,
        rebillable: shouldRebill,
        expense_date: date,
        supplier: supplier.trim(),
        description: description.trim(),
        total_cents: totalCents,
        entered_gst_cents: gstCents,
        business_use_percentage: businessUsePercentage,
        notes: notes.trim() || null,
        created_by: user.id,
      };
      const existing = editingId
        ? rows.find((row) => row.id === editingId)
        : null;
      const operation = editingId
        ? db
            .from("expenses")
            .update(values)
            .eq("id", editingId)
            .select()
            .single()
        : db.from("expenses").insert(values).select().single();
      const { data, error } = await operation;
      if (error || !data) throw error ?? new Error("Expense was not saved.");
      if (receipt) {
        try {
          const metadata = await uploadReceipt(
            data.id,
            existing?.receipt_path ?? null,
          );
          if (metadata) {
            const { error: metadataError } = await db
              .from("expenses")
              .update(metadata)
              .eq("id", data.id);
            if (metadataError) throw metadataError;
          }
        } catch (receiptError) {
          Alert.alert(
            "Expense saved",
            `Receipt upload failed. Edit the expense to retry. ${receiptError instanceof Error ? receiptError.message : ""}`,
          );
        }
      }
      resetForm();
      await load();
    } finally {
      setSaving(false);
    }
  }

  async function addCategory() {
    if (!workspaceId || !newCategory.trim()) return;
    const { error } = await db.from("expense_categories").insert({
      workspace_id: workspaceId,
      name: newCategory.trim(),
      sort_order: categories.length * 10 + 10,
    });
    if (error) Alert.alert("Could not add category", error.message);
    else {
      setNewCategory("");
      await load();
    }
  }

  async function remove(row: Expense) {
    await removeFile(row.receipt_path);
    const { error } = await db.from("expenses").delete().eq("id", row.id);
    if (error) Alert.alert("Could not delete expense", error.message);
    else
      setRows((current) =>
        current.filter((candidate) => candidate.id !== row.id),
      );
  }

  async function openReceipt(row: Expense) {
    if (!row.receipt_path) return;
    try {
      await openExternalUrl(await signedFileUrl(row.receipt_path));
    } catch (error) {
      Alert.alert(
        "Could not open receipt",
        error instanceof Error ? error.message : "Try again.",
      );
    }
  }

  async function exportCsv() {
    await saveCsvExport("mintstint-expenses.csv", [
      [
        "date",
        "supplier",
        "description",
        "category",
        "total NZD",
        "entered GST",
        "business-use %",
        "claimable total",
        "claimable GST",
        "notes",
        "receipt",
      ],
      ...filtered.map((row) => [
        row.expense_date,
        row.supplier,
        row.description,
        row.expense_categories?.name ?? "",
        (row.total_cents / 100).toFixed(2),
        (row.entered_gst_cents / 100).toFixed(2),
        String(row.business_use_percentage),
        ((row.claimable_total_cents ?? 0) / 100).toFixed(2),
        ((row.claimable_gst_cents ?? 0) / 100).toFixed(2),
        row.notes ?? "",
        row.receipt_filename ?? "",
      ]),
    ]);
  }

  const claimableTotal = filtered.reduce(
    (sum, row) => sum + (row.claimable_total_cents ?? 0),
    0,
  );
  const claimableGst = filtered.reduce(
    (sum, row) => sum + (row.claimable_gst_cents ?? 0),
    0,
  );

  return (
    <ProtectedScreen
      title="Expenses"
      subtitle="Keep seven-year-ready expense records and receipt evidence. Tax treatment still needs accountant review."
    >
      {!canWrite && !membership.isLoading ? (
        <Card gap="xs">
          <AppText weight="semibold">Accountant view-only access</AppText>
          <AppText size="xs">
            You can review and export all expense records and open stored
            receipts.
          </AppText>
        </Card>
      ) : null}
      {canWrite ? (
        <Card gap="md">
          <AppText weight="semibold">
            {editingId ? "Edit expense" : "Add expense"}
          </AppText>
          <View style={styles.formGrid}>
            <TextField
              grow
              value={date}
              onChangeText={setDate}
              placeholder="YYYY-MM-DD"
            />
            <TextField
              grow
              value={supplier}
              onChangeText={setSupplier}
              placeholder="Supplier"
            />
            <TextField
              grow
              value={description}
              onChangeText={setDescription}
              placeholder="Description"
            />
            <SearchableSelect
              title="Expense category"
              value={categoryId}
              onValueChange={setCategoryId}
              placeholder="Choose category"
              options={[
                { label: "Uncategorised", value: "" },
                ...categories.map((category) => ({
                  label: category.name,
                  value: category.id,
                })),
              ]}
            />
            <SearchableSelect
              title="Client for rebill"
              value={clientId}
              onValueChange={(value) => {
                setClientId(value);
                setProjectId("");
              }}
              placeholder="No client"
              options={clientOptions}
            />
            <SearchableSelect
              title="Project for rebill"
              value={projectId}
              onValueChange={setProjectId}
              placeholder="No project"
              options={projectOptions}
            />
            <SearchableSelect
              title="Invoice this cost to client?"
              value={rebillable}
              onValueChange={setRebillable}
              placeholder="No"
              options={[
                { label: "No", value: "false" },
                { label: "Yes, add to a client invoice", value: "true" },
              ]}
            />
            <TextField
              grow
              value={total}
              onChangeText={setTotal}
              placeholder="Total NZD"
              keyboardType="decimal-pad"
            />
            <TextField
              grow
              value={gst}
              onChangeText={setGst}
              placeholder="GST included NZD"
              keyboardType="decimal-pad"
            />
            <TextField
              grow
              value={businessUse}
              onChangeText={setBusinessUse}
              placeholder="Business use %"
              keyboardType="decimal-pad"
            />
          </View>
          <TextField
            value={notes}
            onChangeText={setNotes}
            placeholder="Notes"
            multiline
          />
          <View style={styles.actions}>
            <Button variant="outline" onPress={() => void pickReceipt()}>
              <ButtonText>
                {receipt
                  ? receipt.name
                  : editingId
                    ? "Replace/add receipt"
                    : "Attach receipt"}
              </ButtonText>
            </Button>
            <Button onPress={() => void save()} disabled={saving}>
              <ButtonText>
                {saving
                  ? "Saving..."
                  : editingId
                    ? "Save changes"
                    : "Save & add another"}
              </ButtonText>
            </Button>
            {editingId ? (
              <Button variant="outline" onPress={resetForm}>
                <ButtonText>Cancel</ButtonText>
              </Button>
            ) : null}
          </View>
          <View style={styles.actions}>
            <TextField
              grow
              value={newCategory}
              onChangeText={setNewCategory}
              placeholder="New custom category"
            />
            <Button
              compact
              variant="outline"
              disabled={!newCategory.trim()}
              onPress={() => void addCategory()}
            >
              <ButtonText>Add category</ButtonText>
            </Button>
          </View>
        </Card>
      ) : null}
      <Card gap="md">
        <AppText weight="semibold">Find and export</AppText>
        <View style={styles.formGrid}>
          <TextField
            grow
            value={from}
            onChangeText={setFrom}
            placeholder="From YYYY-MM-DD"
          />
          <SearchableSelect
            title="Filter expense category"
            value={categoryFilter}
            onValueChange={setCategoryFilter}
            placeholder="All categories"
            options={[
              { label: "All categories", value: "" },
              ...categories.map((category) => ({
                label: category.name,
                value: category.id,
              })),
            ]}
          />
          <SearchableSelect
            title="Filter client billing"
            value={rebillFilter}
            onValueChange={setRebillFilter}
            placeholder="All expenses"
            options={[
              { label: "All expenses", value: "" },
              { label: "Ready to rebill", value: "ready" },
              { label: "Already rebilled", value: "rebilled" },
            ]}
          />
          <TextField
            grow
            value={to}
            onChangeText={setTo}
            placeholder="To YYYY-MM-DD"
          />
          <TextField
            grow
            value={query}
            onChangeText={setQuery}
            placeholder="Search supplier, description, category"
          />
        </View>
        <View style={styles.summary}>
          <AppText>Claimable total {money(claimableTotal)}</AppText>
          <AppText>Claimable GST {money(claimableGst)}</AppText>
          <Button variant="outline" compact onPress={() => void exportCsv()}>
            <ButtonText>Export CSV</ButtonText>
          </Button>
        </View>
      </Card>
      <Stack gap="sm">
        {filtered.map((row) => (
          <Card key={row.id} gap="sm">
            <View style={styles.summary}>
              <View style={styles.grow}>
                <AppText weight="semibold">{row.supplier}</AppText>
                <AppText size="xs">
                  {row.description} ·{" "}
                  {row.expense_categories?.name ?? "Uncategorised"} ·{" "}
                  {row.expense_date}
                </AppText>
                {row.rebillable ? (
                  <AppText size="xs">
                    {row.rebilled_invoice_id
                      ? "Rebilled to invoice"
                      : `Ready to rebill · ${clientById.get(row.client_id ?? "")?.name ?? "Client"}${row.project_id ? ` · ${projectById.get(row.project_id)?.name ?? "Project"}` : ""}`}
                  </AppText>
                ) : null}
                <AppText size="xs">
                  {money(row.total_cents)} · GST {money(row.entered_gst_cents)}{" "}
                  · {row.business_use_percentage}% business · claimable{" "}
                  {money(row.claimable_total_cents ?? 0)}
                </AppText>
                {row.notes ? <AppText size="xs">{row.notes}</AppText> : null}
              </View>
              <View style={styles.actions}>
                {row.receipt_path ? (
                  <Button
                    compact
                    variant="outline"
                    onPress={() => void openReceipt(row)}
                  >
                    <ButtonText>Receipt</ButtonText>
                  </Button>
                ) : null}
                {canWrite ? (
                  <Button compact variant="outline" onPress={() => edit(row)}>
                    <ButtonText>Edit</ButtonText>
                  </Button>
                ) : null}
                {canWrite ? (
                  <Button
                    compact
                    variant="outline"
                    onPress={() =>
                      Alert.alert(
                        "Delete expense?",
                        "This also removes its stored receipt.",
                        [
                          { text: "Cancel", style: "cancel" },
                          {
                            text: "Delete",
                            style: "destructive",
                            onPress: () => void remove(row),
                          },
                        ],
                      )
                    }
                  >
                    <ButtonText>Delete</ButtonText>
                  </Button>
                ) : null}
              </View>
            </View>
          </Card>
        ))}
      </Stack>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    formGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
      alignItems: "center",
    },
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
      alignItems: "center",
    },
    summary: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
      alignItems: "center",
      justifyContent: "space-between",
    },
    grow: { flex: 1, minWidth: 220, gap: spacing.xs },
    receipt: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
    },
  });
